import { Injectable, computed, inject, signal } from '@angular/core';
import { httpsCallable } from 'firebase/functions';

import { StaffVenue } from '../models/staff.model';
import { FirebaseService } from './firebase.service';
import { OrgContextService } from './org-context.service';

const STORAGE_KEY = 'accord.staffVenue';

/**
 * Which venue(s) does the signed-in user work at? Resolved server-side by the
 * accordGetMyStaffContext callable (it can scan every venue's staffIndex/{uid},
 * which client rules can't), so this works even for users whose org role only
 * grants 'accord.staff'.
 *
 * Contract: accordGetMyStaffContext {organizationId}
 *   → {venues: [{venueId, venueName, employeeId, timezone}]}
 */
@Injectable({
  providedIn: 'root',
})
export class StaffContextService {
  private readonly firebase = inject(FirebaseService);
  private readonly orgContext = inject(OrgContextService);

  readonly venues = signal<StaffVenue[]>([]);
  readonly selectedVenue = signal<StaffVenue | null>(null);
  readonly loading = signal(false);

  readonly employeeId = computed(() => this.selectedVenue()?.employeeId ?? null);

  private loadedOrgId: string | null = null;
  private inflight: Promise<void> | null = null;

  /** Resolve the staff context for the current org (once per org; safe to re-call). */
  async ensureLoaded(): Promise<void> {
    const orgId = this.orgContext.currentOrg()?.id;
    if (!orgId) return;
    if (this.loadedOrgId === orgId) return;
    if (this.inflight) return this.inflight;

    this.inflight = this.load(orgId).finally(() => {
      this.inflight = null;
    });
    return this.inflight;
  }

  private async load(orgId: string): Promise<void> {
    this.loading.set(true);
    try {
      const callable = httpsCallable<{ organizationId: string }, { venues: StaffVenue[] }>(
        this.firebase.functions,
        'accordGetMyStaffContext',
      );
      const result = await callable({ organizationId: orgId });
      const venues = result.data.venues ?? [];

      this.venues.set(venues);
      const savedId = localStorage.getItem(STORAGE_KEY);
      this.selectedVenue.set(venues.find((v) => v.venueId === savedId) ?? venues[0] ?? null);
      this.loadedOrgId = orgId;
    } finally {
      this.loading.set(false);
    }
  }

  selectVenue(venueId: string): void {
    const venue = this.venues().find((v) => v.venueId === venueId);
    if (!venue) return;
    this.selectedVenue.set(venue);
    localStorage.setItem(STORAGE_KEY, venueId);
  }
}
